(() => {
  const OVERLAY_SELECTOR = '[data-search-overlay]';
  const FOCUSABLE_SELECTOR = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';
  const FALLBACK_VIEW = 'predictive-json';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_DELAY = 220;
  const getAdaptiveDebounce = (length) => {
    if (length >= 5) return 100;
    if (length >= 3) return 150;
    return 250;
  };
  const PRODUCTS_LIMIT = 20;
  const SEARCH_FIELDS = ['title', 'product_type', 'vendor', 'tag', 'variants.title', 'body'];
  const PRODUCT_INDEX_PAGE_SIZE = 250;
  const ESCAPE_LOOKUP = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };

  const SYNONYM_MAP = {
    kurczak: ['kurczak', 'kurczaka', 'kurczaki', 'kurczakiem', 'kurczę', 'z kurczaka', 'chicken'],
    kaczka: ['kaczka', 'kaczki', 'kaczką', 'kacze', 'kaczym', 'z kaczki', 'duck'],
    indyk: ['indyk', 'indyka', 'indycze', 'indyczym', 'turkey'],
    wołowina: ['wołowina', 'wolowina', 'wołowe', 'wołowym', 'beef'],
    łosoś: ['łosoś', 'losos', 'łososia', 'łososiem', 'salmon'],
    ryba: ['ryba', 'ryby', 'rybą', 'fish'],
    pies: ['pies', 'dla psa', 'psa', 'psem', 'dog'],
    kot: ['kot', 'dla kota', 'kota', 'kotem', 'cat'],
    przysmak: ['przysmak', 'przysmaki', 'przysmakiem', 'smaczki', 'treats'],
    liofilizowane: ['liofilizowane', 'liofilizowany', 'liofilizowanym', 'freeze dried'],
    mieszane: ['mieszane', 'mieszany', 'mieszanym', 'mieszanka', 'blend', 'mix', 'miks', 'mixed']
  };

  const CART_ICON = '<svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M20.7269 24.0784H7.28688C6.04928 24.0784 5.04688 23.076 5.04688 21.8384V10.0784C5.04688 9.15108 5.79952 8.39844 6.72688 8.39844H21.2869C22.2142 8.39844 22.9669 9.15108 22.9669 10.0784V21.8384C22.9669 23.076 21.9645 24.0784 20.7269 24.0784Z" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.4808 12.0847C18.3262 12.0847 18.2008 12.2101 18.2019 12.3647C18.2019 12.5192 18.3273 12.6447 18.4819 12.6447C18.6364 12.6447 18.7619 12.5192 18.7619 12.3647C18.7619 12.2101 18.6364 12.0847 18.4808 12.0847" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.52763 12.0847C9.37307 12.0847 9.24763 12.2101 9.24875 12.3647C9.24875 12.5192 9.37419 12.6447 9.52875 12.6447C9.68331 12.6447 9.80875 12.5192 9.80875 12.3647C9.80875 12.2101 9.68331 12.0847 9.52763 12.0847" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/><path d="M9.5312 8.39406V8.11406V8.11406C9.5312 5.79454 11.4117 3.91406 13.7312 3.91406H14.2912C16.6108 3.91406 18.4912 5.79454 18.4912 8.11406V8.11406V8.39406" stroke="#2D4F40" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ESCAPE_LOOKUP[character] || character);

  const formatMoney = (cents) => {
    const value = Number.parseInt(cents, 10);
    if (Number.isNaN(value)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(value);
      } catch (error) {
        // Fallback to manual formatting below.
      }
    }
    const amount = value / 100;
    const currency = window.Shopify?.currency?.active || 'PLN';
    if (currency === 'EUR') {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
        .format(amount).replace(/€\s*([\d.,]+)/, '$1 €');
    }
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' })
      .format(amount).replace('PLN', 'zł');
  };

  const removeDiacritics = (value) => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L');

  const normalizeQuery = (value) => removeDiacritics(String(value || '').toLowerCase().trim());

  const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ');

  // Check if product has force-unavailable tags
  const isForceUnavailable = (product) => {
    const tags = product?.tags;
    if (!tags) return false;
    const tagList = Array.isArray(tags) ? tags : String(tags).split(',');
    const normalizedTags = tagList.map((tag) => String(tag).trim().toLowerCase());
    return normalizedTags.some((tag) =>
      tag === 'force-unavailable' ||
      tag === 'force_unavailable' ||
      tag === 'niedostepny' ||
      tag === 'unavailable'
    );
  };

  const uniqueNormalizedTerms = (input) => {
    const set = new Set();
    input.forEach((value) => {
      const normalized = normalizeQuery(value);
      if (normalized) set.add(normalized);
    });
    return Array.from(set);
  };

  let _cachedTranslations = null;
  const getTranslations = () => {
    if (_cachedTranslations) return _cachedTranslations;
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (!overlay) return { addToCart: 'Dodaj do koszyka', unavailable: 'W produkcji' };

    try {
      const translationsJson = overlay.getAttribute('data-translations');
      if (translationsJson) {
        const translations = JSON.parse(translationsJson);
        _cachedTranslations = {
          addToCart: translations.add_to_cart || 'Dodaj do koszyka',
          unavailable: translations.unavailable || 'W produkcji'
        };
        return _cachedTranslations;
      }
    } catch (error) {
      console.warn('Failed to parse translations:', error);
    }

    _cachedTranslations = { addToCart: 'Dodaj do koszyka', unavailable: 'W produkcji' };
    return _cachedTranslations;
  };

  const getMarketPrefix = () => {
    // Active market root from server-rendered window.shopMarket (AYL-129): correct in
    // subfolder mode ("/de-de" -> "de-de") and subdomain mode ("" -> null). Never the path.
    const root = (window.shopMarket && window.shopMarket.root) || '';
    const seg = root.replace(/^\/+/, '').split('/')[0].toLowerCase();
    return /^[a-z]{2}(-[a-z]{2})?$/.test(seg) ? seg : null;
  };

  const getCurrentLanguage = () => {
    const htmlLang = (document.documentElement && document.documentElement.getAttribute('lang')) || '';
    const htmlLangCode = String(htmlLang).toLowerCase().split('-')[0].trim();
    if (/^[a-z]{2}$/.test(htmlLangCode)) return htmlLangCode;

    const marketPrefix = getMarketPrefix();
    if (marketPrefix) return marketPrefix.split('-')[0];

    // Default storefront language (no prefix) in this theme setup is Polish.
    return 'pl';
  };

  const getProductLocaleTags = (product) => {
    const rawTags = product?.tags ?? product?.tag ?? [];
    const tags = Array.isArray(rawTags)
      ? rawTags
      : (typeof rawTags === 'string' ? rawTags.split(',').map((t) => t.trim()).filter(Boolean) : []);

    const localeTags = new Set(); // kept for backwards-compat; no longer populated
    const languageTags = new Set(); // e.g. "de"
    let isGlobal = false;

    tags.forEach((tag) => {
      const normalized = String(tag)
        .toLowerCase()
        .trim()
        // Remove accents/diacritics to support tags like "français"
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[_\s]+/g, '-'); // normalize similarly to Shopify handles

      // Explicit opt-out of locale filtering (show in all markets)
      if (normalized === 'global' || normalized === 'all-markets' || normalized === 'all' || normalized === 'intl') {
        isGlobal = true;
        return;
      }

      // Human-readable language tags → ISO language codes (exact match only).
      // This avoids the old bug where we matched parts of words (e.g. "ayla-delight" → "de").
      const languageNameMap = {
        german: 'de',
        french: 'fr',
        swedish: 'sv',
        english: 'en',
        polish: 'pl'
      };
      if (languageNameMap[normalized]) {
        languageTags.add(languageNameMap[normalized]);
        return;
      }
    });

    return { localeTags, languageTags, isGlobal };
  };

  const shouldHideProductForCurrentLocale = (product) => {
    if (!product) return false;

    const currentLang = getCurrentLanguage();
    const currentMarket = getMarketPrefix(); // may be null on default market
    const { localeTags, languageTags, isGlobal } = getProductLocaleTags(product);

    if (isGlobal) return false;

    // Untagged products are treated as globally available (shown in all markets).
    // Use language-specific tags (e.g. "polish", "german") to restrict a product to one market.
    if (!localeTags.size && !languageTags.size) {
      return false;
    }

    // If it has language tags, only show it when it matches current language.
    if (languageTags.has(currentLang)) return false;

    return true;
  };

  const makeMarketAwareUrl = (url) => {
    if (!url) return url;
    
    const marketPrefix = getMarketPrefix();
    if (!marketPrefix) return url;
    
    // If URL already has market prefix, return as-is
    if (url.startsWith(`/${marketPrefix}/`)) return url;
    
    // If URL starts with /products/, add market prefix
    if (url.startsWith('/products/')) {
      return `/${marketPrefix}${url}`;
    }
    
    // If URL starts with /, add market prefix (for other paths)
    if (url.startsWith('/') && !url.startsWith('//')) {
      return `/${marketPrefix}${url}`;
    }
    
    return url;
  };

  const buildProductMarkup = (product, config) => {
    if (!product) return '';
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((item) => item && item.available) || variants[0];
    if (!variant || !variant.id) return '';

    const placeholder = config.placeholderImage;
    const successIcon = config.successIcon;
    const title = escapeHtml(product.title || '');
    const rawUrl = product.url || `/products/${product.handle || ''}`;
    const url = escapeHtml(makeMarketAwareUrl(rawUrl));
    const imageSrc = product.featured_image?.url || product.image?.url || placeholder;
    const imageUrl = escapeHtml(imageSrc || placeholder || '');
    const price = formatMoney(variant.price || product.price);
    const successIconAttr = escapeHtml(successIcon || '');
    // Check both variant availability and force-unavailable tags
    const variantAvailable = variant?.available !== false;
    const forceUnavailable = isForceUnavailable(product);
    const isAvailable = variantAvailable && !forceUnavailable;
    const availabilityAttr = isAvailable ? '' : ' disabled aria-disabled="true"';

    const translations = getTranslations();
    const buttonText = isAvailable ? translations.addToCart : translations.unavailable;

    return `
      <article class="search-result-card" data-search-result>
        <a class="search-result-image" href="${url}">
          <img src="${imageUrl}" alt="${title}" loading="lazy" width="236" height="236">
        </a>
        <div class="search-result-info">
          <a class="search-result-title" href="${url}">${title}</a>
          <form class="search-result-form" action="/cart/add" method="post">
            <input type="hidden" name="id" value="${escapeHtml(variant.id)}">
            <button type="submit" class="search-result-add" data-success-icon="${successIconAttr}"${availabilityAttr}>
              <span class="search-add-content">
                ${CART_ICON}
                <span class="add-to-cart-text">${escapeHtml(buttonText)}</span>
              </span>
              <span class="search-result-price">${price}</span>
            </button>
          </form>
        </div>
      </article>
    `;
  };

  const buildSynonymQueue = (query) => {
    const trimmed = String(query || '').trim();
    const normalized = normalizeQuery(trimmed);
    if (!normalized) return [];

    const queue = [];
    const seen = new Set();

    const enqueue = (value) => {
      const clean = normalizeQuery(value);
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      queue.push({ raw: value.trim(), normalized: clean });
    };

    enqueue(trimmed);
    if (normalized !== trimmed.toLowerCase()) enqueue(normalized);

    const tokens = normalized.split(/\s+/).filter(Boolean);
    tokens.forEach((token) => {
      const directMatch = SYNONYM_MAP[token];
      if (directMatch) {
        directMatch.forEach(enqueue);
        return;
      }
      // Prefix matching for partial words (e.g. "kacz" → "kaczka" group)
      Object.entries(SYNONYM_MAP).forEach(([key, synonyms]) => {
        const nKey = normalizeQuery(key);
        if (nKey.startsWith(token) || token.startsWith(nKey)) {
          synonyms.forEach(enqueue);
        }
      });
    });

    // Also try individual tokens themselves for broader matches.
    tokens.forEach(enqueue);

    return queue;
  };

  const buildApiQueue = (queue, maxCalls = 3) => {
    if (queue.length <= maxCalls) return queue;
    const picks = [];
    const step = (queue.length - 1) / (maxCalls - 1);
    for (let i = 0; i < maxCalls; i++) {
      picks.push(queue[Math.round(i * step)]);
    }
    return picks;
  };

  const normalizeVariants = (variants, fallbackPrice) => {
    if (!Array.isArray(variants)) return [];
    return variants.map((variant) => ({
      id: variant?.id,
      price: typeof variant?.price === 'number' ? variant.price : fallbackPrice,
      available: typeof variant?.available !== 'undefined' ? variant.available : true
    }));
  };

  const normalizeProducts = (products) => {
    if (!Array.isArray(products)) return [];
    return products
      .filter((product) => {
        // Hide products tagged for other locales/languages
        try {
          if (shouldHideProductForCurrentLocale(product)) return false;
        } catch (error) {
          // Fail open
        }
        return true;
      })
      .map((product) => {
        const tags = product?.tags ?? product?.tag;
        const searchFragments = [
          product?.title,
          product?.product_type,
          product?.vendor,
          product?.body,
          product?.body_html,
          Array.isArray(tags) ? tags.join(' ') : tags
        ];
        const searchText = normalizeQuery(stripHtml(searchFragments.filter(Boolean).join(' ')));

        return {
          title: product?.title,
          url: product?.url,
          handle: product?.handle,
          price: product?.price,
          featured_image: product?.featured_image,
          image: product?.image,
          tags: tags, // Preserve tags for filtering/dedupe
          variants: normalizeVariants(product?.variants, product?.price),
          _searchText: searchText
        };
      });
  };

  const mapStorefrontProduct = (product) => {
    if (!product) return null;
    
    // Hide products tagged for other locales/languages
    try {
      if (shouldHideProductForCurrentLocale(product)) return null;
    } catch (error) {
      // Fail open
    }
    
    const featuredImage = product.image || product.images?.[0];
    const variants = Array.isArray(product.variants) ? product.variants.map((variant) => ({
      id: variant?.id,
      price: variant?.price ? Math.round(parseFloat(variant.price) * 100) : product.price,
      available: variant?.available
    })) : [];
    const primaryVariant = variants[0];
    const price = primaryVariant?.price || (product.price ? Math.round(parseFloat(product.price) * 100) : 0);

    const searchFragments = [
      product.title,
      product.product_type,
      product.vendor,
      product.body_html,
      Array.isArray(product.tags) ? product.tags.join(' ') : product.tags
    ];

    const searchText = normalizeQuery(stripHtml(searchFragments.filter(Boolean).join(' ')));

    const rawUrl = product.handle ? `/products/${product.handle}` : product.url || '';
    const url = makeMarketAwareUrl(rawUrl);

    return {
      title: product.title,
      url: url,
      handle: product.handle,
      price,
      featured_image: featuredImage ? { url: featuredImage.src || featuredImage.url, alt: featuredImage.alt || '' } : null,
      image: featuredImage ? { url: featuredImage.src || featuredImage.url, alt: featuredImage.alt || '' } : null,
      tags: product.tags, // Preserve tags for filtering
      variants,
      _searchText: searchText,
      _fromIndex: true
    };
  };

  document.addEventListener('DOMContentLoaded', () => {
    const overlay = document.querySelector(OVERLAY_SELECTOR);
    if (!overlay) return;

    const toggles = document.querySelectorAll('[data-search-toggle]');
    const mobileMenuOverlay = document.querySelector('[data-mobile-menu-overlay]');
    const background = overlay.querySelector('[data-search-background]');
    const closeButton = overlay.querySelector('[data-search-close]');
    const clearButton = overlay.querySelector('[data-search-clear]');
    const input = overlay.querySelector('[data-search-input]');
    const resultsGrid = overlay.querySelector('[data-search-results]');
    const resultsContainer = overlay.querySelector('[data-search-panel]');
    const emptyState = overlay.querySelector('[data-search-empty]');
    const loadingState = overlay.querySelector('[data-search-loading]');
    const errorState = overlay.querySelector('[data-search-error]');
    const emptyMessage = emptyState ? emptyState.querySelector('p') : null;
    const defaultEmptyMessage = emptyMessage ? emptyMessage.textContent : '';
    const noResultsTemplate = overlay.getAttribute('data-no-results-template') || '';
    const config = {
      successIcon: overlay.getAttribute('data-success-icon') || '',
      placeholderImage: overlay.getAttribute('data-placeholder-image') || ''
    };
    const body = document.body;
    const language = document.documentElement && document.documentElement.getAttribute('lang') || '';

    let debounceTimer = null;
    let activeController = null;
    let activeSearchToken = 0;

    const predictiveCache = new Map();
    const fallbackCache = new Map();
    let productIndexPromise = null;
    let cachedFuseIndex = null;
    let cachedLocaleFiltered = null;

    const setState = (state) => {
      if (resultsGrid) resultsGrid.hidden = state !== 'results';
      if (loadingState) loadingState.hidden = state !== 'loading';
      if (emptyState) emptyState.hidden = state !== 'empty';
      if (errorState) errorState.hidden = state !== 'error';
    };

    const formatNoResultsMessage = (query) => {
      const terms = String(query || '').trim();
      if (!terms) return defaultEmptyMessage || '';
      if (noResultsTemplate) return noResultsTemplate.replace('__terms__', terms);
      return defaultEmptyMessage || '';
    };

    const showEmpty = (message) => {
      if (emptyMessage) emptyMessage.textContent = message;
      if (resultsGrid) resultsGrid.innerHTML = '';
      setState('empty');
    };

    const showError = () => {
      if (resultsGrid) resultsGrid.innerHTML = '';
      setState('error');
    };

    const renderProducts = (products, originalQuery, { silentEmpty = false } = {}) => {
      if (!Array.isArray(products) || !products.length) {
        if (!silentEmpty) {
          showEmpty(formatNoResultsMessage(originalQuery));
        }
        return false;
      }

      const markup = products.map((product) => buildProductMarkup(product, config)).filter(Boolean).join('');
      if (!markup.trim()) {
        if (!silentEmpty) {
          showEmpty(formatNoResultsMessage(originalQuery));
        }
        return false;
      }

      resultsGrid.innerHTML = markup;
      if (resultsContainer) resultsContainer.scrollTop = 0;
      setState('results');
      return true;
    };

    const buildPredictiveUrl = (query) => {
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(PRODUCTS_LIMIT));
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', SEARCH_FIELDS.join(','));
      params.set('resources[options][variants][fields]', 'title');
      if (language) params.set('lang', language);
      const root = window.Shopify?.routes?.root || '/';
      return `${root}search/suggest.json?${params.toString()}`;
    };

    const buildFallbackUrl = (query) => {
      const params = new URLSearchParams();
      params.set('view', FALLBACK_VIEW);
      params.set('q', query);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      if (language) params.set('lang', language);
      const root = window.Shopify?.routes?.root || '/';
      return `${root}search?${params.toString()}`;
    };

    const queryPredictive = async (variant) => {
      if (predictiveCache.has(variant.normalized)) {
        return predictiveCache.get(variant.normalized);
      }
      try {
        const response = await fetch(buildPredictiveUrl(variant.raw), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: activeController?.signal
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const payload = await response.json();
        const products = normalizeProducts(payload?.resources?.results?.products);
        predictiveCache.set(variant.normalized, products);
        return products;
      } catch (error) {
        console.error('Predictive search error:', error);
        return null;
      }
    };

    // The fallback view (search.predictive-json) is not part of the theme; Shopify then answers
    // with the normal HTML search page. Detect that once and stop requesting it for this page view.
    let fallbackUnavailable = false;

    const queryFallback = async (variant) => {
      if (fallbackUnavailable) return null;
      if (fallbackCache.has(variant.normalized)) {
        return fallbackCache.get(variant.normalized);
      }
      try {
        const response = await fetch(buildFallbackUrl(variant.raw), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: activeController?.signal
        });
        if (!response.ok) throw new Error(`Fallback search failed: ${response.status}`);
        // Shopify answers with HTML (even labelled application/json) when the view is missing.
        const text = await response.text();
        const trimmed = text.trimStart();
        if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
          fallbackUnavailable = true;
          return null;
        }
        const payload = JSON.parse(text);
        const products = normalizeProducts(payload?.products);
        fallbackCache.set(variant.normalized, products);
        return products;
      } catch (error) {
        console.error('Fallback search error:', error);
        return null;
      }
    };

    const loadProductIndex = async () => {
      if (productIndexPromise) return productIndexPromise;
      productIndexPromise = (async () => {
        const results = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          try {
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(`${root}products.json?limit=${PRODUCT_INDEX_PAGE_SIZE}&page=${page}`, {
              method: 'GET',
              headers: { Accept: 'application/json' }
            });
            if (!response.ok) break;
            const payload = await response.json();
            const products = Array.isArray(payload?.products) ? payload.products : [];
            products.forEach((item) => {
              const mapped = mapStorefrontProduct(item);
              if (mapped) results.push(mapped);
            });
            hasMore = products.length === PRODUCT_INDEX_PAGE_SIZE;
            page += 1;
          } catch (error) {
            console.error('Error loading product index:', error);
            break;
          }
        }

        return results;
      })();

      return productIndexPromise;
    };

    const searchLocalIndex = async (variant) => {
      try {
        const index = await loadProductIndex();
        if (!Array.isArray(index) || !index.length) return [];

        // Cache locale-filtered list across calls within the same session
        if (!cachedLocaleFiltered) {
          cachedLocaleFiltered = index.filter((product) => {
            if (!product) return false;
            try {
              if (shouldHideProductForCurrentLocale(product)) return false;
            } catch (error) {
              // Fail open
            }
            return true;
          });
        }

        // Use Fuse.js if available — cache the index across variant calls
        if (window.AylaTheme?.fuseSearch?.searchProducts) {
          if (!cachedFuseIndex) {
            cachedFuseIndex = window.AylaTheme.fuseSearch.createProductIndex(cachedLocaleFiltered);
          }
          if (cachedFuseIndex) {
            return window.AylaTheme.fuseSearch.searchProducts(cachedFuseIndex, variant.raw, PRODUCTS_LIMIT * 2);
          }
        }

        // Fallback to basic search if Fuse.js not loaded
        const baseTerms = [variant.raw, variant.normalized];
        const tokens = variant.normalized.split(/\s+/).filter(Boolean);
        baseTerms.push(...tokens);

        tokens.forEach((token) => {
          const synonyms = SYNONYM_MAP[token];
          if (synonyms) baseTerms.push(...synonyms);
        });

        const normalizedTerms = uniqueNormalizedTerms(baseTerms);
        if (!normalizedTerms.length) return [];

        return cachedLocaleFiltered.filter((product) => {
          if (!product?._searchText) return false;
          return normalizedTerms.some((term) => product._searchText.includes(term));
        });
      } catch (error) {
        console.error('Local index search error:', error);
        return [];
      }
    };

    // On non-default markets some products may lack translations, meaning
    // their title in /de-de/products.json is identical to the default-language
    // title in /products.json.  We detect these by comparing titles and filter
    // them from local-index search results to prevent cross-language leakage.
    const loadDefaultProductTitles = (() => {
      let promise = null;
      return () => {
        if (promise) return promise;
        promise = (async () => {
          try {
            const resp = await fetch('/products.json?limit=250', {
              method: 'GET',
              headers: { Accept: 'application/json' }
            });
            if (!resp.ok) return new Map();
            const data = await resp.json();
            const titles = new Map();
            (data.products || []).forEach((p) => {
              if (p?.handle) titles.set(p.handle, p.title);
            });
            return titles;
          } catch (error) {
            return new Map();
          }
        })();
        return promise;
      };
    })();

    // Enrich variant data for products already in the pool that lack
    // renderable variants (the Predictive Search API sometimes omits them).
    const enrichVariantsFromIndex = async (pool) => {
      if (!pool || pool.size === 0) return;
      // Collect handles of products that lack renderable variants.
      const needsVariants = new Map();
      pool.forEach((product, key) => {
        const handle = product?.handle;
        if (!handle) return;
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const hasId = variants.some((v) => v && v.id);
        if (!hasId) needsVariants.set(handle, key);
      });
      if (!needsVariants.size) return;

      try {
        const index = await loadProductIndex();
        if (!Array.isArray(index) || !index.length) return;
        index.forEach((indexProduct) => {
          if (!indexProduct?.handle) return;
          const poolKey = needsVariants.get(indexProduct.handle);
          if (!poolKey) return;
          const existing = pool.get(poolKey);
          if (!existing) return;
          const indexVariants = Array.isArray(indexProduct.variants) ? indexProduct.variants : [];
          if (indexVariants.some((v) => v && v.id)) {
            existing.variants = indexVariants;
          }
        });
      } catch (error) {
        // Fail silently – products remain without variants but are still linkable.
      }
    };

    const getProductKey = (product) => product?.handle || product?.url || product?.title || '';

    const hasRenderableVariant = (product) => {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      return variants.some((variant) => variant && variant.id);
    };

    const hasAvailableVariant = (product) => {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      return variants.some((variant) => variant && variant.available);
    };

    const scoreProduct = (product, normalizedQuery, normalizedTerms) => {
      // Use Fuse.js scoring if available
      if (window.AylaTheme?.fuseSearch?.isStrongMatch) {
        const isStrong = window.AylaTheme.fuseSearch.isStrongMatch(product, normalizedQuery, 0.6);
        if (!isStrong) {
          // Weak match - heavily penalize
          return hasAvailableVariant(product) ? -50 : -100;
        }
      }

      const titleNorm = normalizeQuery(product?.title || '');
      const searchText = product?._searchText || titleNorm;

      let score = 0;

      // Exact title match - highest priority
      if (titleNorm === normalizedQuery) {
        score += 100;
      } else if (normalizedQuery && titleNorm.startsWith(normalizedQuery)) {
        score += 80;
      } else if (normalizedQuery && titleNorm.includes(normalizedQuery)) {
        // Check if it's a word boundary match vs substring
        const wordBoundaryRegex = new RegExp(`\\b${normalizedQuery}\\b`, 'i');
        if (wordBoundaryRegex.test(titleNorm)) {
          score += 60; // Word boundary match in title
        } else {
          score += 20; // Substring only - lower score
        }
      }

      // Check terms with word boundary preference
      normalizedTerms.forEach((term) => {
        if (!term || !searchText.includes(term)) return;

        const wordBoundaryRegex = new RegExp(`\\b${term}\\b`, 'i');
        if (wordBoundaryRegex.test(searchText)) {
          score += 15; // Word boundary match
        } else {
          score += 3; // Substring only
        }
      });

      score += hasAvailableVariant(product) ? 5 : -5;
      return score;
    };

    const mergeUniqueProducts = (targetMap, products) => {
      if (!Array.isArray(products)) return;
      products.forEach((product) => {
        if (!product) return;
        const key = getProductKey(product);
        if (!key) return;
        const existing = targetMap.get(key);
        if (!existing) {
          targetMap.set(key, product);
          return;
        }
        // Never replace an API-sourced product with an index product —
        // index products may have handles that don't resolve on this market.
        if (!existing._fromIndex && product._fromIndex) return;
        const existingRenderable = hasRenderableVariant(existing);
        const incomingRenderable = hasRenderableVariant(product);
        if (!existingRenderable && incomingRenderable) {
          targetMap.set(key, product);
          return;
        }
        // Prefer the entry that has an available variant (better UX for quick add).
        if (!hasAvailableVariant(existing) && hasAvailableVariant(product)) {
          targetMap.set(key, product);
        }
      });
    };

    // --- Product URL validation for multi-market stores ---
    // Products from the local index may have handles that don't resolve
    // on the current market (e.g. Polish handles on /de-de/).
    // Validate with a lightweight fetch and cache results per session.
    const validatedHandleCache = new Map();

    const validateProductUrls = async (products) => {
      const root = window.Shopify?.routes?.root || '/';
      const toValidate = products.filter(
        (p) => p._fromIndex && p.handle && !validatedHandleCache.has(p.handle)
      );

      if (toValidate.length > 0) {
        await Promise.allSettled(
          toValidate.map(async (p) => {
            try {
              const resp = await fetch(`${root}products/${p.handle}.json`);
              validatedHandleCache.set(p.handle, resp.ok);
            } catch {
              validatedHandleCache.set(p.handle, true); // Fail open on network error
            }
          })
        );
      }

      return products.filter((p) => {
        if (!p._fromIndex) return true;
        if (!p.handle) return true;
        return validatedHandleCache.get(p.handle) !== false;
      });
    };

    const executeSearchQueueMerged = async (queue, originalQuery, token) => {
      const normalizedQuery = normalizeQuery(originalQuery);
      const normalizedTerms = uniqueNormalizedTerms([normalizedQuery, ...normalizedQuery.split(/\s+/).filter(Boolean)]);

      const pool = new Map();
      const maxPoolSize = PRODUCTS_LIMIT * 4;
      const getRenderableCount = () => {
        let count = 0;
        pool.forEach((product) => {
          if (hasRenderableVariant(product)) count += 1;
        });
        return count;
      };

      setState('loading');

      // Limit API calls to max 3 evenly-spread variants (first, middle, last)
      const apiQueue = buildApiQueue(queue);

      // 1) Predictive across variants — ALL IN PARALLEL
      const predictiveResults = await Promise.allSettled(
        apiQueue.map((variant) => queryPredictive(variant))
      );
      if (token !== activeSearchToken) return;
      predictiveResults.forEach((result) => {
        if (result.status === 'fulfilled') mergeUniqueProducts(pool, result.value);
      });

      // Progressive render: show results immediately after Phase 1
      if (pool.size > 0) {
        await enrichVariantsFromIndex(pool);
        if (token !== activeSearchToken) return;
        let earlyResults;
        if (window.AylaTheme?.fuseSearch?.rankProducts) {
          earlyResults = window.AylaTheme.fuseSearch.rankProducts(
            Array.from(pool.values()), originalQuery, PRODUCTS_LIMIT
          );
        } else {
          earlyResults = Array.from(pool.values())
            .map((product) => ({ product, score: scoreProduct(product, normalizedQuery, normalizedTerms) }))
            .sort((a, b) => b.score - a.score)
            .map((entry) => entry.product)
            .slice(0, PRODUCTS_LIMIT);
        }
        if (earlyResults.length > 0) {
          renderProducts(earlyResults, originalQuery, { silentEmpty: true });
        }
      }

      // 2) Fallback search if we still don't have enough — ALL IN PARALLEL
      if (getRenderableCount() < PRODUCTS_LIMIT) {
        const fallbackResults = await Promise.allSettled(
          apiQueue.map((variant) => queryFallback(variant))
        );
        if (token !== activeSearchToken) return;
        fallbackResults.forEach((result) => {
          if (result.status === 'fulfilled') mergeUniqueProducts(pool, result.value);
        });
      }

      // 3) Local product index text search (uses cached Fuse.js index).
      if (getRenderableCount() < PRODUCTS_LIMIT) {
        const marketPrefix = getMarketPrefix();
        const defaultTitles = marketPrefix ? await loadDefaultProductTitles() : null;
        for (const variant of queue) {
          if (token !== activeSearchToken) return;
          let results = await searchLocalIndex(variant);
          if (token !== activeSearchToken) return;
          if (results && defaultTitles && defaultTitles.size > 0) {
            results = results.filter((p) => {
              const defaultTitle = defaultTitles.get(p.handle);
              return !defaultTitle || p.title !== defaultTitle;
            });
          }
          mergeUniqueProducts(pool, results);
          if (getRenderableCount() >= maxPoolSize) break;
        }
      }
      if (pool.size > 0) {
        await enrichVariantsFromIndex(pool);
      }

      if (token !== activeSearchToken) return;

      // Final ranking using position-aware scoring
      let sorted;
      if (window.AylaTheme?.fuseSearch?.rankProducts) {
        sorted = window.AylaTheme.fuseSearch.rankProducts(
          Array.from(pool.values()),
          originalQuery,
          PRODUCTS_LIMIT
        );
      } else {
        sorted = Array.from(pool.values())
          .map((product) => ({ product, score: scoreProduct(product, normalizedQuery, normalizedTerms) }))
          .sort((a, b) => b.score - a.score || String(a.product?.title || '').localeCompare(String(b.product?.title || '')))
          .map((entry) => entry.product)
          .slice(0, PRODUCTS_LIMIT);
      }

      sorted = await validateProductUrls(sorted);
      if (token !== activeSearchToken) return;

      const rendered = renderProducts(sorted, originalQuery, { silentEmpty: false });
      if (!rendered) {
        showEmpty(formatNoResultsMessage(originalQuery));
      }
    };

    const resetPanel = () => {
      clearTimeout(debounceTimer);
      debounceTimer = null;
      if (activeController) {
        activeController.abort();
        activeController = null;
      }
      if (input) input.value = '';
      if (clearButton) clearButton.hidden = true;
      if (resultsGrid) {
        resultsGrid.innerHTML = '';
        resultsGrid.hidden = true;
      }
      showEmpty(defaultEmptyMessage);
    };

    const handleFocusTrap = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(overlay.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        if (element.hasAttribute('hidden')) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        if (element.offsetParent === null && element !== document.activeElement) return false;
        return true;
      });
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const openOverlay = (trigger) => {
      overlay.classList.add('active');
      overlay.setAttribute('aria-hidden', 'false');
      body.style.overflow = 'hidden';
      overlay.addEventListener('keydown', handleFocusTrap);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        input?.focus({ preventScroll: true });
      }, 50);
    };

    const closeOverlay = () => {
      overlay.classList.remove('active');
      overlay.setAttribute('aria-hidden', 'true');
      body.style.overflow = '';
      overlay.removeEventListener('keydown', handleFocusTrap);
      document.querySelectorAll('[data-search-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
      resetPanel();
    };

    const handleInput = () => {
      if (!input) return;
      const query = input.value.trim();
      if (clearButton) clearButton.hidden = query.length === 0;

      if (query.length < MIN_QUERY_LENGTH) {
        showEmpty(defaultEmptyMessage);
        return;
      }

      const queue = buildSynonymQueue(query);
      if (!queue.length) {
        showEmpty(formatNoResultsMessage(query));
        return;
      }

      activeSearchToken += 1;
      const token = activeSearchToken;
      if (activeController) activeController.abort();
      activeController = new AbortController();
      setState('loading');
      executeSearchQueueMerged(queue, query, token).catch((error) => {
        console.error('Search overlay error:', error);
        if (token === activeSearchToken) {
          showError();
        }
      });
    };

    const closeMobileMenu = () => {
      if (!mobileMenuOverlay) return;
      if (!mobileMenuOverlay.classList.contains('active')) return;
      mobileMenuOverlay.classList.remove('active');
      body.style.overflow = '';
    };

    toggles.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closeMobileMenu();
        openOverlay(button);
      });
    });

    if (background) {
      background.addEventListener('click', () => {
        closeOverlay();
      });
    }

    if (closeButton) {
      closeButton.addEventListener('click', () => {
        closeOverlay();
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        if (input) {
          input.value = '';
          input.focus({ preventScroll: true });
        }
        resetPanel();
      });
    }

    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        const queryLength = (input.value || '').trim().length;
        debounceTimer = setTimeout(handleInput, getAdaptiveDebounce(queryLength));
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && overlay.classList.contains('active')) {
        event.preventDefault();
        closeOverlay();
      }
    });

    // Preload product index and default titles in background
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => {
        loadProductIndex();
        if (getMarketPrefix()) loadDefaultProductTitles();
      });
    } else {
      setTimeout(() => {
        loadProductIndex();
        if (getMarketPrefix()) loadDefaultProductTitles();
      }, 2000);
    }

    resetPanel();
  });
})();
